import styled, { css } from 'styled-components';

import { getCDNImageUrl } from '@/helpers/cdn';

import { device, typography } from '@/styles/helpers';

const prizeIconUrl = getCDNImageUrl('/common/prizeIcon.svg');

interface ScRegistrationProps {
  withMobPadding?: boolean;
}

export const ScRegistration = styled.div<ScRegistrationProps>`
  position: relative;
  width: 100%;
  margin: 8px auto;
  justify-content: flex-start;
  align-items: center;
  display: grid;
  grid-template-columns: 1fr;
  max-width: 480px;

  @media (${device.mobile}) {
    padding: 16px 8px 62px;
  }
`;

export const ScRegistrationContent = styled.div<ScRegistrationProps>`
  width: 100%;
  flex: auto;
  color: ${({ theme }) => theme.blackColor};
  background: ${({ theme }) => theme.whiteColor};
  border-radius: 24px;
  padding: 32px 32px 0;
  position: relative;

  @media (${device.tablet}) {
    max-width: 900px;
    padding: 32px 32px 0;
  }

  @media (${device.mobile}) {
    width: 100%;
    margin: 0;
    padding: 32px 16px 0;
  }

  & > .header {
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    & > h2 {
      font-weight: bold;
      font-size: 32px;
      line-height: 40px;
      text-transform: uppercase;
      text-align: center;
      letter-spacing: 0.96px;

      @media (${device.mobile}) {
        font-size: 28px;
        text-align: center;
        letter-spacing: 0.84px;
        line-height: 36px;
        word-break: break-word;
      }
    }

    & > .description {
      text-align: center;
      font-size: 16px;
      line-height: 24px;
    }
  }
`;

export const ScRegistrationWrapButtons = styled.div<{ cols?: number }>`
  display: grid;
  grid-template-columns: ${({ cols }) => `repeat(${cols || 2}, 1fr)`};
  gap: 8px;
  align-items: center;
  margin-bottom: 32px;

  .left-col,
  .right-col {
    width: 100%;
    display: flex;
  }
`;

export const ScRegistrationInfo = styled.div`
  text-align: center;

  margin: 32px -32px 0 -32px;
  padding: 12px;
  font-size: 16px;
  line-height: 24px;
  background-color: ${({ theme }) => theme.gray1Color};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
  color: ${({ theme }) => theme.blackColor};

  button {
    min-width: auto;
    padding: 0 13px 0 0;
    overflow: visible;

    .text {
      font-weight: 700;

      font-size: 16px;
      line-height: 24px;
    }

    .awareWrap {
      display: none;
    }

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      width: 6px;
      height: 100%;
      background-color: ${({ theme }) => theme.secondaryColor};
      -webkit-mask-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDYgMTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNSA2TDEgMTEiIHN0cm9rZT0iIzI1QUFENiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==);
      mask-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDYgMTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNSA2TDEgMTEiIHN0cm9rZT0iIzI1QUFENiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==);
      mask-size: contain;
      -webkit-mask-size: contain;
      mask-repeat: no-repeat;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-position: center;
      mask-position: center;
      transition: all 0.3s;
    }

    @media (hover: hover) {
      &:hover {
        &::after {
          background-color: ${({ theme }) => theme.secondaryColorHover};
          right: -3px;
        }
      }
    }
  }

  @media (${device.mobile}) {
    margin: 32px -16px 0 -16px;
  }
`;

export const ScFormNavigation = styled.div<{ withMarginBottom: boolean }>`
  position: relative;
  z-index: 0;
  margin-bottom: ${({ withMarginBottom }) => withMarginBottom && '32px'};

  & > ul {
    list-style: none;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;

    & > li {
      position: relative;
      margin: 0 0 0 12px;

      &:not(:last-child) {
        &:before {
          content: '';
          display: block;
          width: 40px;
          height: 12px;
          background-color: ${({ theme }) => theme.gray1Color};
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          left: 6px;
        }
      }
    }
    & > li:not(:last-child),
    li:not(:first-child) {
      margin: 0 10px;
    }
  }
`;

export enum StatusItem {
  ACTIVE = 1,
  COMPLETED,
  NOT_ACTIVE,
  FINISH,
}

interface ScFormNavigationItemProps {
  status: StatusItem;
}

export const ScFormNavigationItem = styled.div<ScFormNavigationItemProps>`
  position: relative;
  z-index: 1;
  background-color: ${({ theme }) => theme.gray1Color};
  color: ${({ theme }) => theme.whiteColor};
  width: ${({ status }) => (status === StatusItem.FINISH ? '30px' : '20px')};
  height: ${({ status }) => (status === StatusItem.FINISH ? '30px' : '20px')};
  border-radius: 50%;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    width: 16px;
    height: 16px;

    ${({ status, theme }) => {
      switch (status) {
        case StatusItem.ACTIVE:
        case StatusItem.COMPLETED:
          return css`
            background-color: ${theme.mainColor};
          `;
        case StatusItem.NOT_ACTIVE:
          return css`
            background-color: ${theme.grayMegaLightColor};
          `;
        case StatusItem.FINISH:
          return css`
            width: 26px;
            height: 26px;
            background-color: ${theme.mainColor};
            background-image: url(${prizeIconUrl});
            background-size: 16px 15px;
            background-position: center;
            background-repeat: no-repeat;
            z-index: 1;
          `;
        default:
          return '';
      }
    }}
  }

  .step {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    transform: translate(-50%, -50%);
    font-weight: 700;
    font-size: 10px;
    line-height: 14px;
    z-index: 2;
  }
`;

export const ScRegistrationBanner = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;
  overflow: hidden;
  border-radius: 16px;
  z-index: 1;

  & > .bg {
    object-fit: cover;
    object-position: top center;
    z-index: -1;
  }

  & > .offer-container {
    display: flex;
    padding: 16px 0 16px 16px;
    flex-direction: column;
    justify-content: center;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.16px;
    color: ${({ theme }) => theme.whiteColor};

    & > h2 {
      font-size: inherit;
      letter-spacing: inherit;
      line-height: inherit;
      align-self: flex-start;
      color: ${({ theme }) => theme.secondaryColor};
      margin-bottom: 8px;
    }

    & > .offer-1 {
      font-size: 22px;
      line-height: 28px;
      letter-spacing: 0.44px;

      @media (${device.mobile}) {
        font-size: 20px;
        line-height: 26px;
        letter-spacing: 0.4px;
      }

      @media (max-width: 379px) {
        font-size: inherit;
        line-height: inherit;
        letter-spacing: inherit;
      }
    }
  }
`;

export const ScPromoCheckboxesContainer = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  margin-left: 32px;
  gap: 12px;
`;

export const ScChangeCountryMessage = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;

  svg {
    min-width: 24px;
    min-height: 24px;
  }

  svg path {
    fill: ${({ theme }) => theme.blackColor};
  }

  .message {
    ${typography['caption1regular']}
    margin-left: 4px;
  }

  .message b {
    ${typography['caption1bold']}
    color: ${({ theme }) => theme.secondaryColor};
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const ScRegistrationFormRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 8px;
  margin-bottom: 5px;
  width: 100%;
`;
